import { useEffect, useRef } from 'react'

interface FallBeamBackgroundProps {
  className?: string
  lineCount?: number
  beamColorClass?: string
}

// All Tailwind 400-level colors that the AI can pick
const colorMap: Record<string, string> = {
  slate: 'rgba(148, 163, 184, 0.8)',
  gray: 'rgba(156, 163, 175, 0.8)',
  red: 'rgba(248, 113, 113, 0.8)',
  rose: 'rgba(251, 113, 133, 0.8)',
  orange: 'rgba(251, 146, 60, 0.8)',
  amber: 'rgba(251, 191, 36, 0.8)',
  yellow: 'rgba(250, 204, 21, 0.8)',
  lime: 'rgba(163, 230, 53, 0.8)',
  green: 'rgba(74, 222, 128, 0.8)',
  emerald: 'rgba(52, 211, 153, 0.8)',
  teal: 'rgba(45, 212, 191, 0.8)',
  cyan: 'rgba(34, 211, 238, 0.8)',
  sky: 'rgba(56, 189, 248, 0.8)',
  blue: 'rgba(96, 165, 250, 0.8)',
  indigo: 'rgba(129, 140, 248, 0.8)',
  violet: 'rgba(167, 139, 250, 0.8)',
  purple: 'rgba(192, 132, 252, 0.8)',
  fuchsia: 'rgba(232, 121, 249, 0.8)',
  pink: 'rgba(244, 114, 182, 0.8)',
}

/** Accepts either "blue" or "blue-400" and returns the RGBA value. */
function getColorValue(colorClass: string): string {
  const base = colorClass.replace(/-\d+$/, '') // strip suffix like -400
  return colorMap[base] ?? colorMap['cyan']
}

export function FallBeamBackground({
  className = '',
  lineCount = 20,
  beamColorClass = 'cyan',
}: FallBeamBackgroundProps) {
  const containerRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const container = containerRef.current
    if (!container) return

    const render = () => {
      container.querySelectorAll('.fall-beam-line').forEach((l) => l.remove())
      const glowColor = getColorValue(beamColorClass)

      for (let i = 1; i <= lineCount; i++) {
        const line = document.createElement('div')
        line.classList.add('fall-beam-line')

        const left = `${i * (100 / lineCount) + Math.random() * 5 - 5}%`
        const duration = `${12 + Math.random() * 12}s`
        const delay = `${-Math.random() * 20}s`

        line.style.left = left
        line.style.setProperty('--ani-duration', duration)
        line.style.setProperty('--ani-delay', delay)
        line.style.setProperty('--beam-glow-color', glowColor)

        container.appendChild(line)
      }
    }

    render()

    const observer = new MutationObserver(() => render())
    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ['class'],
    })

    return () => {
      observer.disconnect()
      container.querySelectorAll('.fall-beam-line').forEach((l) => l.remove())
    }
  }, [lineCount, beamColorClass])

  return (
    <>
      <style>{`
        .fall-beam-line {
          position: absolute;
          width: 1px;
          height: 100%;
          z-index: 0;
        }
        .fall-beam-line::after {
          content: "";
          position: absolute;
          left: 0;
          width: 100%;
          height: 80px;
          background: linear-gradient(
            to bottom,
            rgba(255, 255, 255, 0),
            var(--beam-glow-color)
          );
          animation: fall var(--ani-duration) var(--ani-delay) linear infinite;
        }
        @keyframes fall {
          0% { top: -100px; }
          100% { top: 100%; }
        }
      `}</style>
      <div
        ref={containerRef}
        className={`absolute inset-0 z-0 overflow-hidden bg-transparent ${className}`}
      />
    </>
  )
}
